/**
 * One status vocabulary for agents and their runs: colour tokens, labels, and
 * the small dot components the list, runs table and canvas share.
 *
 * (Merged from WorkflowEditor/execStatus.js, which now re-exports this.)
 * Colours are the app's feedback tokens, so they follow light/dark.
 */
import React from 'react';

export { formatExecDuration } from './format';

export const EXEC_STATUS_COLOR = {
  succeeded: 'var(--feedback-success)',
  failed:    'var(--feedback-error)',
  running:   'var(--feedback-info)',
  queued:    'var(--text-disabled)',
  cancelled: 'var(--text-muted)',
  skipped:   'var(--text-disabled)',
  idle:      'var(--text-disabled)',
};

export function execStatusColor(status) {
  return EXEC_STATUS_COLOR[status] || 'var(--text-muted)';
}

export const EXEC_STATUS_LABEL = {
  succeeded: 'Succeeded',
  failed: 'Failed',
  running: 'Running',
  queued: 'Queued',
  cancelled: 'Cancelled',
  skipped: 'Skipped',
};

export function execStatusLabel(status) {
  return EXEC_STATUS_LABEL[status] || (status ? String(status) : '');
}

/** Runs still going (the UI treats queued as running). */
export const isRunActive = (status) => status === 'running' || status === 'queued';

export const AGENT_STATUS_LABEL = { active: 'Active', paused: 'Paused', draft_only: 'Draft' };

export function agentStatusLabel(status) {
  return AGENT_STATUS_LABEL[status] || 'Draft';
}

const dotBase = { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box' };

/** Agent status: active = filled success, paused = hollow, draft = dim filled. */
export function AgentStatusDot({ status, className }) {
  const label = agentStatusLabel(status);
  let style;
  if (status === 'active') style = { ...dotBase, background: 'var(--feedback-success)' };
  else if (status === 'paused') style = { ...dotBase, border: '1px solid var(--text-disabled)' };
  else style = { ...dotBase, background: 'var(--text-disabled)', opacity: 0.6 };
  return <span role="img" aria-label={label} title={label} className={className} style={style} />;
}

/** Run status: filled in its token; queued is hollow; running pulses (see .agents-run-pulse). */
export function RunStatusDot({ status, className }) {
  const label = execStatusLabel(status) || 'Unknown';
  const color = execStatusColor(status);
  const style = status === 'queued'
    ? { ...dotBase, border: `1px solid ${color}` }
    : { ...dotBase, background: color };
  const cls = [status === 'running' ? 'agents-run-pulse' : '', className || ''].filter(Boolean).join(' ') || undefined;
  return <span role="img" aria-label={label} title={label} className={cls} style={style} />;
}
